import { AppRouteRecordRaw } from '../types';
import { ETranslationModule } from '@/i18n/translation';

const Body = () => import('@/components/body.vue');
const MyFleetDriverManagement = () =>
  import('@/pages/fleetManagement/myFleet/drivers/DriverManagement.vue');
const MyFleetVehicleManagement = () =>
  import('@/pages/fleetManagement/myFleet/vehicles/VehicleManagement.vue');
const MyFleetOrderManagement = () =>
  import('@/pages/fleetManagement/myFleet/transactions/OrderManagement.vue');
const FleetPartnerTransactionDetail = () =>
  import('@/pages/fleetManagement/myFleet/transactions/fleet-partner-transaction-detail/FleetPartnerTransactionDetail.vue');
const SubFleetTransactions = () =>
  import('@/pages/fleetManagement/myFleet/transactions/sub-fleet-transactions/SubFleetTransactions.vue');
const SubFleetTransactionDetail = () =>
  import('@/pages/fleetManagement/myFleet/transactions/sub-fleet-transactions/SubFleetTransactionDetail.vue');
const MyFleetDriverLoginAccess = () =>
  import('@/pages/fleetManagement/myFleet/driver-login-access/DriverLoginAccess.vue');
const DriverUploadPage = () => import('@/pages/fleetManagement/csv-upload/DriverUploadPage.vue');
const UploadSessionStatistics = () =>
  import('@/pages/fleetManagement/csv-upload/UploadSessionStatistics.vue');
const CarPoolStatistics = () => import('@/pages/fleetManagement/car-pool/CarPoolStatistics.vue');
const FleetExportCenter = () => import('@/pages/fleetManagement/exports/FleetExportCenter.vue');
const PartnerSavingsCalculatorList = () =>
  import('@/pages/fleetManagement/savings-calculator/partner-savings-calculator-list/PartnerSavingsCalculatorList.vue');
const PartnerSavingsCalculator = () =>
  import('@/pages/fleetManagement/savings-calculator/partner-savings-calculator/PartnerSavingsCalculator.vue');

export const fleetRoutes: AppRouteRecordRaw[] = [
  {
    path: '/fleet',
    component: Body,
    meta: {
      i18nModules: [ETranslationModule.fleet],
      breadcrumb: 'Fleet Management',
    },
    children: [
      {
        path: 'my-fleet/driver-management',
        name: 'driverManagement',
        component: MyFleetDriverManagement,
        meta: {
          title: ' Fleet - Driver Management | ryd Portal',
          breadcrumb: 'Drivers',
          headTitle: 'Driver Management',
          hiddenForSubFleet: true,
        },
      },
      {
        path: 'my-fleet/vehicles',
        name: 'vehicleManagement',
        component: MyFleetVehicleManagement,
        meta: {
          title: ' Fleet - Vehicles | ryd Portal',
          breadcrumb: 'Vehicles',
          headTitle: 'Vehicles',
          hiddenForSubFleet: true,
        },
      },
      {
        path: 'my-fleet/order-management',
        name: 'orderManagement',
        component: MyFleetOrderManagement,
        meta: {
          title: ' Fleet - Order Management | ryd Portal',
          breadcrumb: 'Transactions',
          headTitle: 'Order Management',
          hiddenForSubFleet: true,
        },
      },
      {
        path: 'my-fleet/sf-transactions',
        name: 'subFleetTransactions',
        component: SubFleetTransactions,
        meta: {
          title: ' Fleet - Transactions | ryd Portal',
          breadcrumb: 'Transactions',
          headTitle: 'Transactions',
          requiresSubFleet: true,
        },
      },
      {
        path: 'my-fleet/sf-transactions/:orderUuid',
        name: 'subFleetTransactionDetail',
        component: SubFleetTransactionDetail,
        meta: {
          title: ' Fleet - Transaction Details | ryd Portal',
          breadcrumb: 'Transaction Details',
          headTitle: 'Transaction Details',
          requiresSubFleet: true,
        },
      },
      {
        // Fleet-partner transaction detail. Fleet partners who open the shared
        // /deeplinks/orders/order-details link are redirected here
        // with the orderUuid/environment query preserved.
        path: 'my-fleet/transaction-detail',
        name: 'fleetTransactionDetail',
        component: FleetPartnerTransactionDetail,
        meta: {
          i18nModules: [ETranslationModule.fleet],
          title: ' Fleet - Transaction Details | ryd Portal',
          breadcrumb: 'Transaction Details',
          headTitle: 'Transaction Details',
        },
      },
      {
        // Backward-compat redirect: Invoices moved to Account Management.
        path: 'my-fleet/invoice-management',
        name: 'invoiceManagement',
        redirect: '/account-management/invoices',
      },
      {
        path: 'export-center',
        name: 'fleetExportCenter',
        component: FleetExportCenter,
        meta: {
          title: 'Fleet - Export Center | ryd Portal',
          breadcrumb: 'Export Center',
          headTitle: 'Export Center',
        },
      },
      {
        path: 'my-fleet/driver-login-access',
        name: 'driverLoginAccess',
        component: MyFleetDriverLoginAccess,
        meta: {
          title: ' Fleet - Driver Login Access | ryd Portal',
          requiresRydEmailDomain: true,
          breadcrumb: 'Driver Login Access',
          headTitle: 'Driver Login Access',
        },
      },
      {
        path: 'my-fleet/add-drivers-via-csv',
        name: 'DriverUploadPage',
        component: DriverUploadPage,
        meta: {
          title: ' Fleet - Add Drivers via CSV | ryd Portal',
          breadcrumb: 'Add Drivers via CSV',
          hiddenForSubFleet: true,
        },
      },
      {
        path: '/fleet-management/csv-upload/statistics/:sessionId',
        name: 'UploadSessionStatistics',
        component: UploadSessionStatistics,
        meta: {
          breadcrumb: 'Upload Session Statistics',
          hiddenForSubFleet: true,
        },
      },
      {
        path: '/fleet-management/car-pool/statistics',
        name: 'CarPoolStatistics',
        component: CarPoolStatistics,
        meta: {
          breadcrumb: 'Car Pool Statistics',
        },
      },
      {
        path: 'savings-calculator',
        alias: ['my-fleet/savings-calculator'],
        name: 'fleetPartnerSavingsCalculator',
        component: PartnerSavingsCalculator,
        meta: {
          title: 'Fleet - Savings Calculator | ryd Portal',
          breadcrumb: 'Savings Calculator',
        },
      },
      {
        path: 'savings-calculator/saved',
        alias: ['my-fleet/savings-calculator/saved'],
        name: 'fleetPartnerSavingsCalculatorList',
        component: PartnerSavingsCalculatorList,
        meta: {
          title: 'Fleet - Saved Calculations | ryd Portal',
          breadcrumb: 'Saved Calculations',
        },
      },
      {
        path: 'fleet-operations/activity-logs',
        name: 'fleetActivityLogs',
        redirect: '/fleet-operations/activity-logs',
      },
      {
        path: 'fleet-operations/spend-analytics',
        name: 'fleetSpendAnalytics',
        redirect: '/fleet-operations/spend-analytics',
      },
      {
        path: 'fleet-administration/analytics-dashboard',
        name: 'fleetAnalyticsDashboard',
        redirect: '/fleet-operations/analytics-dashboard',
      },
      {
        path: 'fleet-administration/partner-list',
        name: 'fleetPartnerList',
        redirect: '/fleet-operations/partner-list',
      },
      {
        path: 'fleet-administration/add-drivers-via-csv',
        name: 'AddFleetDriversViaCsv',
        redirect: '/fleet-operations/add-drivers-via-csv',
      },
      {
        path: 'fleet-administration/how-to-onboard-a-fleet-partner',
        name: 'HowToOnbardAFleetPartner',
        redirect: '/fleet-operations/how-to-onboard-a-fleet-partner',
      },
      {
        path: 'fleet-administration/partners/:id',
        name: 'fleetPartnerDetails',
        redirect: (to) => `/fleet-operations/partners/${to.params.id}`,
      },
      {
        path: 'fleet-administration/create-partner',
        name: 'fleetCreatePartner',
        redirect: '/fleet-operations/create-partner',
      },
      {
        path: 'fleet-administration/partner-audit',
        name: 'fleetPartnerAudit',
        redirect: '/fleet-operations/partner-audit',
      },
    ],
  },
];
